/**
 * Sprint 75 — Assistant IA Clarix avec Tool Use.
 * Chat conversationnel + trace des outils invoqués (transparent = confiance).
 */
import { useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Send, Bot, Wrench, Loader2, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { productLabel } from "@/lib/branding";

const AI_LABEL = productLabel("IA");

const SUGGESTIONS = [
  "Quelles sont mes prochaines échéances légales ?",
  "Combien de factures j'ai émis ces 30 derniers jours ?",
  "Crée un brouillon d'AGO pour l'exercice 2025, tenue le 15 juin 2026",
  "Liste mes dirigeants actifs",
  "Donne-moi le score de conformité de ma société",
];

export default function ClarixAI() {
  const [messages, setMessages] = useState([]); // {role, content, tool_calls?}
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [tools, setTools] = useState([]);
  const scrollRef = useRef(null);

  useEffect(() => {
    api.get("/ai/tools").then((r) => setTools(r.data.tools || []))
       .catch(() => setTools([]));
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight,
                                    behavior: "smooth" });
  }, [messages]);

  const send = async (text) => {
    const question = (text ?? input).trim();
    if (!question || loading) return;
    setInput("");
    setLoading(true);
    setMessages((m) => [...m, { role: "user", content: question }]);
    try {
      const r = await api.post("/ai/chat", {
        message: question, session_id: sessionId,
        history: messages.filter((m) => m.role === "user" || m.role === "assistant")
                          .map((m) => ({ role: m.role, content: m.content })),
      });
      setSessionId(r.data.session_id);
      setMessages((m) => [...m, {
        role: "assistant",
        content: r.data.reply,
        tool_calls: r.data.tool_calls || [],
      }]);
    } catch (e) {
      toast.error(apiError(e));
      setMessages((m) => [...m, {
        role: "assistant",
        content: "Désolé, une erreur est survenue. Réessayez.",
      }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 max-w-5xl mx-auto space-y-4">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center">
          <Sparkles className="h-5 w-5 text-purple-600" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">{AI_LABEL}</h1>
          <p className="text-sm text-slate-500">
            Assistant CFO/juridique avec {tools.length} outils autonomes tenant-scopés.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Chat */}
        <Card className="lg:col-span-3">
          <CardContent className="p-0">
            <div ref={scrollRef}
                 className="h-[500px] overflow-y-auto p-4 space-y-3 bg-slate-50"
                 data-testid="chat-scroll">
              {messages.length === 0 && (
                <div className="text-center py-16">
                  <MessageSquare className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500 text-sm">
                    Posez une question ou cliquez sur une suggestion.
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2 justify-center">
                    {SUGGESTIONS.map((s, i) => (
                      <button key={i}
                              onClick={() => send(s)}
                              className="text-xs bg-white border rounded-full px-3 py-1.5 hover:bg-slate-100 transition"
                              data-testid={`suggestion-${i}`}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                    m.role === "user"
                      ? "bg-blue-600 text-white"
                      : "bg-white border shadow-sm"
                  }`}>
                    {m.role === "assistant" && (
                      <div className="flex items-center gap-1 mb-1 text-purple-600">
                        <Bot className="w-3 h-3" />
                        <span className="text-xs font-medium">{AI_LABEL}</span>
                      </div>
                    )}
                    <p className={`text-sm whitespace-pre-line ${
                      m.role === "user" ? "text-white" : "text-slate-800"
                    }`}>{m.content}</p>
                    {m.tool_calls && m.tool_calls.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-slate-200">
                        <p className="text-xs text-slate-400 flex items-center gap-1 mb-1">
                          <Wrench className="w-3 h-3" />
                          {m.tool_calls.length} outil(s) invoqué(s)
                        </p>
                        {m.tool_calls.map((tc, j) => (
                          <details key={j} className="text-xs">
                            <summary className="cursor-pointer text-slate-500 hover:text-slate-700 py-0.5">
                              <code className="bg-slate-100 rounded px-1">{tc.name}</code>
                            </summary>
                            <pre className="bg-slate-50 mt-1 p-2 rounded text-[10px] overflow-auto max-h-40">
{JSON.stringify(tc.result, null, 2)}
                            </pre>
                          </details>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex items-center gap-2 text-slate-500 text-sm">
                  <Loader2 className="w-4 h-4 animate-spin" />{AI_LABEL} réfléchit…
                </div>
              )}
            </div>

            <div className="border-t p-3 bg-white flex gap-2">
              <Input value={input} onChange={(e) => setInput(e.target.value)}
                     placeholder="Posez votre question…"
                     onKeyDown={(e) => e.key === "Enter" && !loading && send()}
                     disabled={loading}
                     data-testid="ai-input" />
              <Button onClick={() => send()} disabled={loading || !input.trim()}
                       data-testid="ai-send-btn">
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Catalogue outils */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <Wrench className="w-4 h-4 text-emerald-600" />
              {tools.length} outils
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1.5 max-h-[500px] overflow-y-auto">
            {tools.map((t) => (
              <div key={t.name} className="border rounded px-2 py-1.5 hover:bg-slate-50 transition">
                <p className="text-xs font-mono text-purple-700">{t.name}</p>
                <p className="text-xs text-slate-500 leading-tight mt-0.5">{t.description}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
